import React, { useState, useEffect } from 'react';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/ToastContainer';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    role: 'user',
    kioskPin: '',
    homePhone: '',
    mobilePhone: ''
  });
  const [error, setError] = useState('');
  const { toasts, success, error: toastError } = useToast();

  // Migrate records modal state
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [migratingUser, setMigratingUser] = useState(null);
  const [targetUserId, setTargetUserId] = useState('');
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const [migrateError, setMigrateError] = useState(null);
  const [deleteSource, setDeleteSource] = useState(true);
  const [sourceStats, setSourceStats] = useState(null);
  const [targetStats, setTargetStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUserStats = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/users/${userId}/stats`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch stats');
      return await res.json();
    } catch (err) {
      console.error('Failed to fetch user stats:', err);
      return null;
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/users`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(data.users);
    } catch (err) {
      toastError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({ name: '', username: '', email: '', password: '', role: 'user', kioskPin: '', homePhone: '', mobilePhone: '' });
    setError('');
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      username: user.username,
      email: user.email || '',
      password: '',
      role: user.role,
      kioskPin: user.kiosk_pin || '',
      homePhone: user.home_phone || '',
      mobilePhone: user.mobile_phone || '',
      isActive: user.is_active !== false
    });
    setError('');
    setShowModal(true);
  };

  const handleToggleActive = async () => {
    if (!editingUser) return;
    const newActive = !formData.isActive;
    try {
      const res = await fetch(`${API_URL}/users/${editingUser.id}/activate`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: newActive })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update status');
      }
      setFormData({ ...formData, isActive: newActive });
      setUsers(users.map(u =>
        u.id === editingUser.id ? { ...u, is_active: newActive } : u
      ));
      success(newActive ? 'User activated' : 'User deactivated');
    } catch (err) {
      toastError(err.message);
    }
  };

  const generateRandomPIN = () => {
    const num = Math.floor(Math.random() * 10000);
    const pin = String(num).padStart(4, '0');
    setFormData({ ...formData, kioskPin: pin });
  };

  const regeneratePINForUser = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/users/${userId}/kiosk-pin`, {
        method: 'PATCH',
        credentials: 'include'
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to generate PIN');
      }
      const data = await res.json();
      setUsers(users.map(u =>
        u.id === userId ? { ...u, kiosk_pin: data.kiosk_pin } : u
      ));
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleKioskPinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setFormData({ ...formData, kioskPin: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      let res;
      if (editingUser) {
        const body = { name: formData.name, username: formData.username, email: formData.email, role: formData.role, kioskPin: formData.kioskPin, home_phone: formData.homePhone, mobile_phone: formData.mobilePhone };
        if (formData.password) {
          body.password = formData.password;
        }
        res = await fetch(`${API_URL}/users/${editingUser.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body)
        });
      } else {
        res = await fetch(`${API_URL}/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(formData)
        });
      }

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Operation failed');
      }

      success(editingUser ? 'User updated successfully' : 'User created successfully');
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const res = await fetch(`${API_URL}/users/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete user');
      }

      success('User deleted successfully');
      fetchUsers();
    } catch (err) {
      toastError(err.message);
    }
  };

  // Migrate records handlers
  const openMigrateModal = async (user) => {
    setMigratingUser(user);
    setTargetUserId('');
    setMigrateResult(null);
    setMigrateError(null);
    setDeleteSource(true);
    setSourceStats(null);
    setTargetStats(null);
    setShowMigrateModal(true);
    // Fetch source user stats
    const stats = await fetchUserStats(user.id);
    if (stats) setSourceStats(stats);
  };

  const closeMigrateModal = () => {
    setShowMigrateModal(false);
    setMigratingUser(null);
    setTargetUserId('');
    setMigrateResult(null);
    setMigrateError(null);
    setDeleteSource(true);
    setSourceStats(null);
    setTargetStats(null);
  };

  // Fetch target stats when target user is selected
  useEffect(() => {
    if (targetUserId) {
      let mounted = true;
      fetchUserStats(parseInt(targetUserId)).then(stats => {
        if (mounted && stats) setTargetStats(stats);
      });
      return () => { mounted = false; };
    } else {
      setTargetStats(null);
    }
  }, [targetUserId]);

  const handleMigrate = async () => {
    if (!targetUserId) {
      setMigrateError('Please select a target user');
      return;
    }

    setMigrateLoading(true);
    setMigrateError(null);

    try {
      const res = await fetch(`${API_URL}/users/migrate-records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          source_user_id: migratingUser.id,
          target_user_id: parseInt(targetUserId),
          delete_source: deleteSource
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to migrate records');
      }
      setMigrateResult(data);
      fetchUsers();
    } catch (err) {
      setMigrateError(err.message);
    } finally {
      setMigrateLoading(false);
    }
  };

  // Other users excluding the one being migrated
  const otherUsers = users.filter(u => u.id !== migratingUser?.id);

  if (loading) {
    return <div className="px-4 py-1 sm:px-0"><p>Loading...</p></div>;
  }

  return (
    <div className="h-full flex flex-col px-4 py-1 sm:px-0">
      <div className="w-full bg-white shadow rounded-lg flex flex-col flex-1 overflow-hidden">
        <div className="flex-shrink-0 p-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Add User
            </button>
          </div>

          <ToastContainer toasts={toasts} />
        </div>

        <div className="flex-1 overflow-auto px-6 pb-6">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Home</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kiosk PIN</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className={user.is_active === false ? 'opacity-50' : ''}>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${user.is_active === false ? 'line-through' : ''}`}>{user.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.username}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.home_phone || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.mobile_phone || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-gray-700 tracking-widest">
                        {user.kiosk_pin || '—'}
                      </span>
                      <button
                        onClick={() => regeneratePINForUser(user.id)}
                        className="text-xs text-indigo-600 hover:text-indigo-900"
                        title="Regenerate PIN"
                      >
                        ⟳
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openEditModal(user)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => openMigrateModal(user)}
                      className="text-orange-600 hover:text-orange-900 mr-4"
                    >
                      Migrate
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingUser ? 'Edit User' : 'Create User'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  autoComplete="off"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Username</label>
                <input
                  type="text"
                  autoComplete="off"
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email (optional)</label>
                <input
                  type="email"
                  autoComplete="off"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Home Phone (optional)</label>
                <input
                  type="tel"
                  autoComplete="off"
                  value={formData.homePhone}
                  onChange={(e) => setFormData({ ...formData, homePhone: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Mobile Phone (optional)</label>
                <input
                  type="tel"
                  autoComplete="off"
                  value={formData.mobilePhone}
                  onChange={(e) => setFormData({ ...formData, mobilePhone: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Password {editingUser ? '(leave blank to keep current)' : ''}
                </label>
                <input
                  type="password"
                  autoComplete="off"
                  required={!editingUser}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Kiosk PIN</label>
                <div className="flex mt-1 rounded-md shadow-sm">
                  <input
                    type="text"
                    autoComplete="off"
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    maxLength={4}
                    placeholder="0000"
                    value={formData.kioskPin}
                    onChange={handleKioskPinChange}
                    className="block flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-l-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-mono tracking-widest"
                  />
                  <button
                    type="button"
                    onClick={generateRandomPIN}
                    className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100"
                    title="Generate random PIN"
                  >
                    ⟳
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex justify-between pt-4 border-t">
                {editingUser && (
                  <button
                    type="button"
                    onClick={handleToggleActive}
                    className={`px-4 py-2 border rounded-md text-sm font-medium ${
                      formData.isActive
                        ? 'bg-red-600 border-red-600 text-white hover:bg-red-700'
                        : 'bg-green-600 border-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {formData.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                )}
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    {editingUser ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Migrate Records Modal */}
      {showMigrateModal && migratingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Migrate User Records</h3>
              <button
                onClick={closeMigrateModal}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Warning Banner */}
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded mb-4 text-sm">
              <strong>Warning:</strong> All records from the source user (left) will be transferred to the destination user (right).
            </div>

            {migrateError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {migrateError}
              </div>
            )}

            {migrateResult ? (
              <>
                <p className="text-sm text-green-600 mb-4">{migrateResult.message}</p>
                <div className="bg-gray-50 rounded p-3 mb-4 max-h-40 overflow-y-auto">
                  {Object.entries(migrateResult.summary || {}).map(([key, count]) => (
                    count > 0 && (
                      <div key={key} className="flex justify-between text-sm py-1">
                        <span className="text-gray-600">{key}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    )
                  ))}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={closeMigrateModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Source Column */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-block w-3 h-3 bg-red-400 rounded-full"></span>
                    <h4 className="font-medium text-gray-700">Source (Records moved)</h4>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div>
                      <h4 className="font-semibold text-gray-900">{migratingUser.name}</h4>
                      <p className="text-sm text-gray-500">{migratingUser.email}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {migratingUser.role} · Created {new Date(migratingUser.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {sourceStats && (
                      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        {sourceStats.transaction_count > 0 && (
                          <div><div className="text-gray-500">Transactions</div><div className="font-semibold">{sourceStats.transaction_count}</div></div>
                        )}
                        {sourceStats.repair_count > 0 && (
                          <div><div className="text-gray-500">Repairs</div><div className="font-semibold">{sourceStats.repair_count}</div></div>
                        )}
                        {sourceStats.appraisal_count > 0 && (
                          <div><div className="text-gray-500">Appraisals</div><div className="font-semibold">{sourceStats.appraisal_count}</div></div>
                        )}
                        {sourceStats.payment_count > 0 && (
                          <div><div className="text-gray-500">Payments</div><div className="font-semibold">{sourceStats.payment_count}</div></div>
                        )}
                        {sourceStats.store_credit_count > 0 && (
                          <div><div className="text-gray-500">Store Credits</div><div className="font-semibold">{sourceStats.store_credit_count}</div></div>
                        )}
                        {sourceStats.message_count > 0 && (
                          <div><div className="text-gray-500">Messages</div><div className="font-semibold">{sourceStats.message_count}</div></div>
                        )}
                        {sourceStats.todos_created > 0 && (
                          <div><div className="text-gray-500">Todos Created</div><div className="font-semibold">{sourceStats.todos_created}</div></div>
                        )}
                        {sourceStats.todos_assigned > 0 && (
                          <div><div className="text-gray-500">Todos Assigned</div><div className="font-semibold">{sourceStats.todos_assigned}</div></div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Destination Column */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-block w-3 h-3 bg-green-400 rounded-full"></span>
                    <h4 className="font-medium text-gray-700">Destination (Records added)</h4>
                  </div>
                  {targetUserId ? (
                    (() => {
                      const target = users.find(u => u.id === parseInt(targetUserId));
                      return (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-900">{target?.name}</h4>
                              <p className="text-sm text-gray-500">{target?.email}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {target?.role} · Created {new Date(target?.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setTargetUserId('')}
                              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                            >
                              ×
                            </button>
                          </div>
                          {targetStats && (
                            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                              {targetStats.transaction_count > 0 && (
                                <div><div className="text-gray-500">Transactions</div><div className="font-semibold">{targetStats.transaction_count}</div></div>
                              )}
                              {targetStats.repair_count > 0 && (
                                <div><div className="text-gray-500">Repairs</div><div className="font-semibold">{targetStats.repair_count}</div></div>
                              )}
                              {targetStats.appraisal_count > 0 && (
                                <div><div className="text-gray-500">Appraisals</div><div className="font-semibold">{targetStats.appraisal_count}</div></div>
                              )}
                              {targetStats.payment_count > 0 && (
                                <div><div className="text-gray-500">Payments</div><div className="font-semibold">{targetStats.payment_count}</div></div>
                              )}
                              {targetStats.store_credit_count > 0 && (
                                <div><div className="text-gray-500">Store Credits</div><div className="font-semibold">{targetStats.store_credit_count}</div></div>
                              )}
                              {targetStats.message_count > 0 && (
                                <div><div className="text-gray-500">Messages</div><div className="font-semibold">{targetStats.message_count}</div></div>
                              )}
                              {targetStats.todos_created > 0 && (
                                <div><div className="text-gray-500">Todos Created</div><div className="font-semibold">{targetStats.todos_created}</div></div>
                              )}
                              {targetStats.todos_assigned > 0 && (
                                <div><div className="text-gray-500">Todos Assigned</div><div className="font-semibold">{targetStats.todos_assigned}</div></div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-4">
                      <select
                        value={targetUserId}
                        onChange={(e) => setTargetUserId(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white"
                      >
                        <option value="">Select a user...</option>
                        {otherUsers.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.name} ({u.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Delete Source Checkbox */}
            {!migrateResult && (
              <div className="mt-4 pt-4 border-t">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deleteSource}
                    onChange={() => setDeleteSource(!deleteSource)}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                  />
                  <span className="text-sm text-red-600 font-medium" onClick={() => setDeleteSource(!deleteSource)}>
                    Delete source user after migration
                  </span>
                </label>
                {deleteSource && (
                  <p className="text-xs text-red-500 mt-1 ml-6">
                    This will permanently remove {migratingUser.name} from the system after records are migrated.
                  </p>
                )}
              </div>
            )}

            {/* Footer Actions */}
            {!migrateResult && (
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={closeMigrateModal}
                  disabled={migrateLoading}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMigrate}
                  disabled={migrateLoading || !targetUserId}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  {migrateLoading ? 'Migrating...' : 'Migrate Records'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
